// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { useMlModels, type MlDataset, type MlModel } from './useMlModels';

/**
 * What each `extract_dataset.py --register` run cut, for the ML developer page.
 *
 * The crops themselves — image, mask and infrared PNGs under `training/data/` — never leave the
 * machine that cut them: the registry row stores a summary and `manifestS3Key` is null. So this
 * is deliberately a description of a dataset rather than a browser for one: what it was selected
 * by, how much of each class it holds, and which models were trained on it. Without it the only
 * answer to "what did that extract run produce" was the job's stdout tail.
 */

/** The classes the registry counts, in the order the detector cares about them. */
const KINDS = ['bunker', 'water', 'green', 'fairway', 'tee', 'trees', 'path'];

export function MlDatasetsPanel({
  apiBaseUrl,
  token,
}: {
  apiBaseUrl: string;
  token: string | null | undefined;
}) {
  const { t } = useI18n();
  const { models, datasets, loading, error } = useMlModels(apiBaseUrl, token, true);

  return (
    <View style={styles.card} testID="ml-datasets-panel">
      <Text style={styles.title}>{t('mlDatasets.title')}</Text>
      <Text style={styles.legend}>{t('mlDatasets.legend')}</Text>

      {loading ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.notice}>{t('mlDatasets.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.notice}>{t('mlDatasets.failed')}</Text>
      ) : datasets.length === 0 ? (
        <Text style={styles.notice} testID="ml-datasets-empty">
          {t('mlDatasets.empty')}
        </Text>
      ) : (
        datasets.map((dataset) => (
          <DatasetRow
            key={dataset.id}
            dataset={dataset}
            // A dataset is judged by what came out of it, so the models trained on it belong on
            // its row: an extract run nothing was ever trained on is the interesting case.
            trainedOn={models.filter((model) => model.datasetId === dataset.id)}
          />
        ))
      )}
    </View>
  );
}

function DatasetRow({ dataset, trainedOn }: { dataset: MlDataset; trainedOn: MlModel[] }) {
  const { t } = useI18n();
  const filter = describeFilter(dataset.filterJson);
  const total = KINDS.reduce((sum, kind) => sum + (dataset.counts[kind] ?? 0), 0);

  return (
    <View style={styles.row} testID={`ml-dataset-${dataset.id}`}>
      <Text style={styles.rowTitle}>{dataset.description || t('mlDatasets.unnamed')}</Text>
      <Text style={styles.meta}>
        {[
          dataset.createdAt ? formatWhen(dataset.createdAt) : null,
          filter.provider,
          filter.imagerySource,
          filter.courseCount !== null ? t('mlDatasets.courses', { count: filter.courseCount }) : null,
        ]
          .filter(Boolean)
          .join('  ·  ')}
      </Text>
      {/* The split is what keeps training and evaluation apart: sets of a size are built from
          one side of it only. A row from before it was recorded shows no line rather than 0/0. */}
      {filter.trainCourses.length > 0 || filter.holdoutCourses.length > 0 ? (
        <Text style={styles.meta} testID={`ml-dataset-split-${dataset.id}`}>
          {t('mlDatasets.split', { train: filter.trainCourses.length, holdout: filter.holdoutCourses.length })}
        </Text>
      ) : null}

      {total > 0 ? (
        <Text style={styles.counts} testID={`ml-dataset-counts-${dataset.id}`}>
          {KINDS.map((kind) => `${t(`courseAnalysis.kind.${kind}`)} ${compact(dataset.counts[kind] ?? 0)}`).join(
            '  ·  '
          )}
        </Text>
      ) : (
        // A registered row with no pixels at all: the run selected courses and cut nothing,
        // which is a result worth seeing rather than an empty line.
        <Text style={styles.warning} testID={`ml-dataset-nopixels-${dataset.id}`}>
          {t('mlDatasets.noPixels')}
        </Text>
      )}

      {/* Whether another machine can train on it at all. A published dataset is the same bytes
          everywhere; one that never left the machine that cut it is that machine's alone. */}
      <Text style={dataset.manifestS3Key ? styles.meta : styles.warning} testID={`ml-dataset-where-${dataset.id}`}>
        {dataset.manifestS3Key ? t('mlDatasets.published') : t('mlDatasets.localOnly')}
      </Text>

      <Text style={styles.meta}>
        {trainedOn.length === 0
          ? t('mlDatasets.trainedNone')
          : t('mlDatasets.trainedOn', {
              models: trainedOn.map((model) => model.nickname ?? `${model.name} ${model.version}`).join(', '),
            })}
      </Text>
    </View>
  );
}

/**
 * What an extract run selected by, out of the filter the registry stored verbatim.
 *
 * Every field is optional on purpose: the filter's shape belongs to `extract_dataset.py`, and a
 * row written by an older (or newer) version of it must still render everything else it has.
 */
export function describeFilter(filterJson: string | null): {
  provider: string | null;
  imagerySource: string | null;
  courseCount: number | null;
  /**
   * The courses the extractor put in the train split and the holdout, by id — empty for a row
   * registered before the split was recorded. What a course set of a given size is built from,
   * and what a hand-made set is checked against: a training set must not reach into the
   * holdout, an evaluation set must not reach into the train split.
   */
  trainCourses: string[];
  holdoutCourses: string[];
} {
  const nothing = { provider: null, imagerySource: null, courseCount: null, trainCourses: [], holdoutCourses: [] };
  if (!filterJson) return nothing;
  let parsed: unknown;
  try {
    parsed = JSON.parse(filterJson);
  } catch {
    return nothing;
  }
  if (!parsed || typeof parsed !== 'object') return nothing;
  const row = parsed as Record<string, unknown>;
  const courses = row.courses;
  const split = row.split && typeof row.split === 'object' ? (row.split as Record<string, unknown>) : {};
  const ids = (value: unknown) => (Array.isArray(value) ? value.filter((c): c is string => typeof c === 'string') : []);
  return {
    provider: typeof row.provider === 'string' ? row.provider : null,
    imagerySource: typeof row.imagerySource === 'string' ? row.imagerySource : null,
    courseCount: Array.isArray(courses) ? courses.length : null,
    trainCourses: ids(split.train),
    holdoutCourses: ids(split.holdout),
  };
}

/**
 * The N richest courses of one split of a dataset, richest first, ties broken by id so the
 * quick set is the same three courses next week. Only courses the training-data list knows are
 * offered: a course in the split that has since lost its labels has nothing to train on.
 */
export function pickBySize(
  splitIds: string[],
  size: number,
  richness: (courseId: string) => number | null
): string[] {
  return splitIds
    .map((id) => ({ id, labels: richness(id) }))
    .filter((c): c is { id: string; labels: number } => c.labels !== null)
    .sort((a, b) => b.labels - a.labels || a.id.localeCompare(b.id))
    .slice(0, Math.max(0, size))
    .map((c) => c.id);
}

/** Pixel counts run to eight figures; the magnitude is the point, not the digits. */
export function compact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

const formatWhen = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 14,
    gap: 10,
  },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  legend: { color: appColors.text.secondary, fontSize: 11, lineHeight: 15 },
  notice: { color: appColors.text.secondary, fontSize: 13 },
  row: {
    borderTopWidth: 1,
    borderTopColor: appColors.surface.input,
    paddingTop: 8,
    gap: 3,
  },
  rowTitle: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  meta: { color: appColors.text.secondary, fontSize: 11 },
  counts: { color: appColors.text.primary, fontSize: 12 },
  warning: { color: appColors.accent.goldMuted, fontSize: 12 },
});
